<template>
  <div class="inline-flex">
    <!-- Trigger button -->
    <button
      @click="openModal"
      :class="['p-2 rounded-lg transition-colors', publishedCount ? 'text-red-500 hover:bg-red-50' : isUploading ? 'text-blue-500' : 'text-gray-400 hover:text-red-500 hover:bg-red-50']"
      :title="buttonTitle"
    >
      <svg v-if="isUploading" class="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="3" opacity="0.2"/><path d="M12 2a10 10 0 019.75 7.75" stroke="currentColor" stroke-width="3" stroke-linecap="round"/></svg>
      <svg v-else class="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
    </button>

    <!-- Publish modal -->
    <Teleport to="body">
      <Transition name="modal">
        <div v-if="showModal" class="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div class="absolute inset-0 bg-black/30 backdrop-blur-sm" />
          <div class="relative bg-white rounded-2xl shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col animate-fade-in">
            <div class="flex items-center justify-between px-6 pt-6 shrink-0">
              <div class="flex items-center gap-3">
                <div class="w-9 h-9 rounded-lg bg-red-50 flex items-center justify-center">
                  <svg class="w-5 h-5 text-red-500" viewBox="0 0 24 24" fill="currentColor"><path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
                </div>
                <div>
                  <h3 class="text-lg font-semibold text-gray-900">Publish to YouTube</h3>
                  <p v-if="form.format_id" class="text-sm text-gray-400">Format: {{ form.format_id }}</p>
                </div>
              </div>
              <button @click="showModal = false" class="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100">
                <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>

            <form @submit.prevent="publish" class="flex-1 flex flex-col overflow-hidden">
              <div class="flex-1 overflow-y-auto p-6 pt-5 space-y-4">
              <!-- Published channels -->
              <div v-if="publishedList.length" class="space-y-2">
                <label class="block text-sm font-medium text-gray-600">Published to</label>
                <div v-for="pub in publishedList" :key="pub.channelId" class="flex items-center justify-between px-3 py-2 rounded-lg bg-green-50 border border-green-100">
                  <div class="flex items-center gap-2 min-w-0">
                    <svg class="w-4 h-4 text-green-500 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                    <span class="text-sm font-medium text-green-700 truncate">{{ getChannelName(pub.channelId) }}</span>
                  </div>
                  <div class="flex items-center gap-2 shrink-0">
                    <a v-if="pub.videoUrl" :href="pub.videoUrl" target="_blank" class="text-xs text-green-600 hover:underline flex items-center gap-1">
                      View <svg class="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                    </a>
                  </div>
                </div>
              </div>

              <!-- Uploading -->
              <div v-if="uploadingList.length" class="space-y-2">
                <div v-for="pub in uploadingList" :key="pub.channelId" class="flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-50 border border-blue-100">
                  <svg class="w-4 h-4 text-blue-500 shrink-0 animate-spin" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="3" opacity="0.2"/><path d="M12 2a10 10 0 019.75 7.75" stroke="currentColor" stroke-width="3" stroke-linecap="round"/></svg>
                  <span class="text-sm font-medium text-blue-700">Uploading to {{ getChannelName(pub.channelId) }}...</span>
                </div>
              </div>

              <!-- Format selector (only when multiple formats and no preselect) -->
              <div v-if="showFormatSelector">
                <label class="block text-sm font-medium text-gray-600 mb-1.5">Format</label>
                <select v-model="form.format_id" required>
                  <option value="" disabled>Select format</option>
                  <option v-for="fmt in formats" :key="fmt.id" :value="fmt.id">{{ fmt.id }}</option>
                </select>
              </div>

              <!-- Channel selector -->
              <div>
                <label class="block text-sm font-medium text-gray-600 mb-1.5">{{ publishedList.length ? 'Publish to another channel' : 'Channel' }}</label>
                <div v-if="availableChannels.length" class="grid grid-cols-6 gap-3">
                  <button v-for="ch in availableChannels" :key="ch.id" type="button" @click="form.channel_id = ch.id"
                    :class="['flex items-center justify-center p-3 rounded-xl border-2 transition-all', form.channel_id === ch.id ? 'border-[var(--color-primary)] bg-[var(--color-primary-light)] shadow-sm' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50']"
                  >
                    <img v-if="ch.thumbnail" :src="ch.thumbnail" :class="['w-14 h-14 rounded-full object-cover', form.channel_id === ch.id ? 'ring-2 ring-[var(--color-primary)] ring-offset-2' : '']" :alt="ch.name" />
                    <div v-else :class="['w-14 h-14 rounded-full bg-red-50 flex items-center justify-center', form.channel_id === ch.id ? 'ring-2 ring-[var(--color-primary)] ring-offset-2' : '']">
                      <span class="text-lg font-bold text-red-500">{{ ch.name?.charAt(0) }}</span>
                    </div>
                  </button>
                </div>
                <p v-else-if="!channels.length" class="text-sm text-gray-400">No channels connected. <router-link to="/publishing" class="text-[var(--color-primary)] hover:underline">Configure publishing</router-link></p>
                <p v-else class="text-sm text-gray-400">Already published to all connected channels.</p>
              </div>

              <!-- Title -->
              <div>
                <label class="block text-sm font-medium text-gray-600 mb-1.5">Title <span class="text-xs text-gray-400 font-normal">(max 100)</span></label>
                <input v-model="form.title" type="text" required maxlength="100" placeholder="Video title" />
              </div>

              <!-- Description -->
              <div>
                <label class="block text-sm font-medium text-gray-600 mb-1.5">Description <span class="text-xs text-gray-400 font-normal">(max 5000)</span></label>
                <textarea v-model="form.description" rows="3" maxlength="5000" placeholder="Video description (optional)" />
              </div>

              <!-- Privacy + Category -->
              <div class="grid grid-cols-2 gap-3">
                <div>
                  <label class="block text-sm font-medium text-gray-600 mb-1.5">Privacy</label>
                  <select v-model="form.privacy">
                    <option value="private">Private</option>
                    <option value="unlisted">Unlisted</option>
                    <option value="public">Public</option>
                  </select>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-600 mb-1.5">Category</label>
                  <select v-model="form.category">
                    <option v-for="cat in ytCategories" :key="cat.id" :value="cat.id">{{ cat.name }}</option>
                  </select>
                </div>
              </div>

              <!-- Language -->
              <div>
                <label class="block text-sm font-medium text-gray-600 mb-1.5">Language</label>
                <select v-model="form.language">
                  <option v-for="lang in ytLanguages" :key="lang.code" :value="lang.code">{{ lang.name }}</option>
                </select>
                <p class="text-xs text-gray-400 mt-1">Applied to video language and title/description language</p>
              </div>

              <!-- Tags -->
              <div>
                <label class="block text-sm font-medium text-gray-600 mb-1.5">Tags <span class="text-xs text-gray-400 font-normal">(comma separated)</span></label>
                <input v-model="form.tags" type="text" placeholder="tag1, tag2, tag3" />
              </div>

              <!-- Made for Kids -->
              <div class="flex items-center justify-between py-1">
                <div>
                  <p class="text-sm font-medium text-gray-700">Made for Kids</p>
                  <p class="text-xs text-gray-400">Required by YouTube for COPPA compliance</p>
                </div>
                <ToggleSwitch v-model="form.madeForKids" />
              </div>

              </div>
              <!-- Submit -->
              <div class="px-6 py-4 border-t border-gray-100 flex justify-end gap-3 shrink-0">
                <button type="button" @click="showModal = false" class="btn-secondary">Cancel</button>
                <button type="submit" :disabled="publishing || !form.channel_id || !form.format_id" class="btn-primary">
                  {{ publishing ? 'Publishing...' : 'Publish' }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </Transition>
    </Teleport>

  </div>
</template>

<script>
import ToggleSwitch from './ToggleSwitch.vue'
import api from '../services/api.js'
import websocket from '../services/websocket.js'
import { notifySuccess, notifyError } from '../services/notifications.js'

export default {
  name: 'YouTubePublishButton',
  components: { ToggleSwitch },
  props: {
    mediaId: { type: String, required: true },
    mediaTitle: { type: String, default: '' },
    mediaDescription: { type: String, default: '' },
    formats: { type: Array, default: () => [] },
    youtubePublishes: { type: Object, default: () => ({}) },
    preselectFormat: { type: String, default: null }
  },
  emits: ['updated'],

  data() {
    return {
      showModal: false,
      publishing: false,
      channels: [],
      localPublishes: {},
      form: {
        channel_id: '',
        format_id: '',
        title: '',
        description: '',
        privacy: 'unlisted',
        category: '24',
        tags: '',
        language: 'es',
        madeForKids: false
      },
      ytCategories: [
        { id: '1', name: 'Film & Animation' },
        { id: '2', name: 'Autos & Vehicles' },
        { id: '10', name: 'Music' },
        { id: '15', name: 'Pets & Animals' },
        { id: '17', name: 'Sports' },
        { id: '19', name: 'Travel & Events' },
        { id: '20', name: 'Gaming' },
        { id: '22', name: 'People & Blogs' },
        { id: '23', name: 'Comedy' },
        { id: '24', name: 'Entertainment' },
        { id: '25', name: 'News & Politics' },
        { id: '26', name: 'Howto & Style' },
        { id: '27', name: 'Education' },
        { id: '28', name: 'Science & Technology' },
        { id: '29', name: 'Nonprofits & Activism' }
      ],
      ytLanguages: [
        { code: 'en', name: 'English' },
        { code: 'es', name: 'Spanish' },
        { code: 'pt', name: 'Portuguese' },
        { code: 'fr', name: 'French' },
        { code: 'de', name: 'German' },
        { code: 'it', name: 'Italian' },
        { code: 'ja', name: 'Japanese' },
        { code: 'ko', name: 'Korean' },
        { code: 'zh', name: 'Chinese' },
        { code: 'ar', name: 'Arabic' },
        { code: 'hi', name: 'Hindi' },
        { code: 'ru', name: 'Russian' },
        { code: 'nl', name: 'Dutch' },
        { code: 'pl', name: 'Polish' },
        { code: 'sv', name: 'Swedish' },
        { code: 'tr', name: 'Turkish' },
        { code: 'th', name: 'Thai' },
        { code: 'vi', name: 'Vietnamese' },
        { code: 'id', name: 'Indonesian' },
        { code: 'fil', name: 'Filipino' }
      ]
    }
  },

  computed: {
    publishes() {
      return { ...this.youtubePublishes, ...this.localPublishes }
    },
    publishedList() {
      return Object.values(this.publishes).filter(p => p.status === 'completed')
    },
    uploadingList() {
      return Object.values(this.publishes).filter(p => p.status === 'uploading')
    },
    publishedCount() {
      return this.publishedList.length
    },
    isUploading() {
      return this.uploadingList.length > 0
    },
    availableChannels() {
      const usedIds = Object.keys(this.publishes).filter(id => {
        const p = this.publishes[id]
        return p.status === 'completed' || p.status === 'uploading'
      })
      return this.channels.filter(ch => !usedIds.includes(ch.id) && !usedIds.includes(ch.channel_id))
    },
    showFormatSelector() {
      return !this.preselectFormat && this.formats.length > 1
    },
    buttonTitle() {
      if (this.publishedCount) return `Published to ${this.publishedCount} channel(s)`
      if (this.isUploading) return 'Uploading to YouTube...'
      return 'Publish to YouTube'
    }
  },

  watch: {
    youtubePublishes: {
      deep: true,
      handler(val) {
        // Sync external updates, clear local overrides for keys that exist externally
        const updated = { ...this.localPublishes }
        for (const key of Object.keys(val || {})) {
          if (updated[key]) delete updated[key]
        }
        this.localPublishes = updated
      }
    }
  },

  mounted() {
    websocket.init()
    websocket.on('youtube:publish:complete', this.onComplete)
    websocket.on('youtube:publish:failed', this.onFailed)
  },

  beforeUnmount() {
    websocket.off('youtube:publish:complete', this.onComplete)
    websocket.off('youtube:publish:failed', this.onFailed)
  },

  methods: {
    onComplete(data) {
      if (data.media_id?.toString() !== this.mediaId?.toString()) return
      this.localPublishes = {
        ...this.localPublishes,
        [data.channel_id]: {
          status: 'completed',
          channelId: data.channel_id,
          videoId: data.video_id,
          videoUrl: data.video_url
        }
      }
      this.$emit('updated', this.publishes)
      notifySuccess(`Published to YouTube: ${this.getChannelName(data.channel_id)}`)
    },

    onFailed(data) {
      if (data.media_id?.toString() !== this.mediaId?.toString()) return
      this.localPublishes = {
        ...this.localPublishes,
        [data.channel_id]: {
          status: 'failed',
          channelId: data.channel_id,
          error: data.error
        }
      }
      this.$emit('updated', this.publishes)
      notifyError(`YouTube publish failed: ${data.error || 'Unknown error'}`)
    },

    async openModal() {
      this.form = {
        channel_id: '',
        format_id: this.preselectFormat || (this.formats.length === 1 ? this.formats[0].id : ''),
        title: this.mediaTitle || '',
        description: this.mediaDescription || '',
        privacy: 'unlisted',
        category: '24',
        tags: '',
        language: 'es',
        madeForKids: false
      }
      try {
        const res = await api.get('/integrations/publishing/youtube/channels', { silent: true })
        this.channels = res.data?.channels || []
      } catch { this.channels = [] }
      this.showModal = true
    },

    getChannelName(channelId) {
      const ch = this.channels.find(c => c.id === channelId || c.channel_id === channelId)
      return ch?.name || channelId
    },

    async publish() {
      if (!this.form.channel_id || !this.form.format_id) return
      this.publishing = true
      try {
        const channelId = this.form.channel_id
        await api.post('/integrations/publishing/youtube/publish', {
          channel_id: channelId,
          media_id: this.mediaId,
          format_id: this.form.format_id,
          title: this.form.title,
          description: this.form.description,
          privacy: this.form.privacy,
          category: this.form.category,
          tags: this.form.tags,
          language: this.form.language,
          madeForKids: this.form.madeForKids
        })
        this.localPublishes = {
          ...this.localPublishes,
          [channelId]: { status: 'uploading', channelId }
        }
        this.form.channel_id = ''
        this.$emit('updated', this.publishes)
        notifySuccess('Video queued for publishing to YouTube')
      } catch (err) {
        notifyError(err?.message || 'Failed to publish')
      } finally {
        this.publishing = false
      }
    },

  }
}
</script>

<style scoped>
.modal-enter-active, .modal-leave-active { transition: opacity 0.2s ease; }
.modal-enter-from, .modal-leave-to { opacity: 0; }
</style>
